<div class="relative flex h-full flex-col">
  <div *ngIf="loading" class="absolute inset-0 z-20 flex items-center justify-center bg-white/60">
    <mat-spinner diameter="40"></mat-spinner>
  </div>

  <div *ngIf="hasProfiles" class="flex shrink-0 flex-col gap-2 px-2.5 pb-1 pt-3.5">
    <!-- min-h-12 matches the editor's title row, so the search box and the profile name line up. -->
    <div class="flex min-h-12 items-center">
      <mat-form-field appearance="outline" class="tl-labelless-field tl-labelless-field--flush w-full">
        <mat-icon matPrefix class="tl-icon-sm text-gray-500">search</mat-icon>
        <input
          matInput
          type="text"
          autocomplete="off"
          [value]="search"
          (input)="onSearch($any($event.target).value)"
          [placeholder]="'Profiles.SearchProfiles' | translate"
          [attr.aria-label]="'Profiles.SearchProfiles' | translate"
        />
        <button
          *ngIf="search"
          matSuffix
          mat-icon-button
          type="button"
          (click)="onSearch('')"
          [attr.aria-label]="'Profiles.ClearSearch' | translate"
        >
          <mat-icon class="tl-icon-sm">close</mat-icon>
        </button>
      </mat-form-field>
    </div>
    <mat-button-toggle-group
      class="tl-segmented w-full"
      [value]="visibility"
      (change)="onVisibility($event.value)"
      [attr.aria-label]="'General.Visibility' | translate"
    >
      <mat-button-toggle class="flex-1" value="all">{{ 'Profiles.All' | translate }}</mat-button-toggle>
      <mat-button-toggle class="flex-1" value="private">{{ 'General.Private' | translate }}</mat-button-toggle>
      <mat-button-toggle class="flex-1" value="public">{{ 'General.Public' | translate }}</mat-button-toggle>
    </mat-button-toggle-group>
  </div>

  <div class="min-h-0 flex-1 overflow-y-auto px-2.5 pb-2.5" role="listbox" [attr.aria-label]="'Settings.ProfilesTitle' | translate">
    <section *ngFor="let group of groups; trackBy: trackGroup" role="group" [attr.aria-label]="group.translateLabel ? (group.label | translate) : group.label">
      <!-- A band, not a caption: with many organizations these are what you scan for. Disabled
           while filtering, when every group with a match is forced open. -->
      <h3 class="sticky top-0 z-10 m-0 bg-white pt-2">
        <button
          type="button"
          class="flex w-full cursor-pointer items-center gap-1.5 rounded-md border-0 bg-surface-muted px-2 py-1.5 text-left font-sans text-sm font-medium text-gray-700 hover:bg-theme-50 disabled:cursor-default disabled:text-gray-700 disabled:hover:bg-surface-muted"
          [disabled]="isFiltering"
          [attr.aria-expanded]="isOpen(group)"
          (click)="toggleGroup(group)"
        >
          <mat-icon class="tl-icon-sm shrink-0 transition-transform" [ngClass]="isOpen(group) ? 'rotate-90' : ''"
            >chevron_right</mat-icon
          >
          <span class="truncate">{{ group.translateLabel ? (group.label | translate) : group.label }}</span>
          <span class="ml-auto shrink-0 rounded-full bg-white px-1.5 text-xs font-normal text-gray-500">{{
            group.profiles.length
          }}</span>
        </button>
      </h3>

      <ng-container *ngIf="isOpen(group)">

      <div
        *ngFor="let profile of group.profiles; trackBy: trackProfile"
        role="option"
        tabindex="0"
        [attr.data-profile-id]="idOf(profile)"
        [attr.aria-selected]="idOf(profile) === editingId"
        (click)="editProfile(profile)"
        (keydown.enter)="editProfile(profile)"
        (keydown.space)="$event.preventDefault(); editProfile(profile)"
        class="group flex cursor-pointer items-center gap-1 rounded-md py-1.5 pl-2 outline-none focus-visible:ring-2 focus-visible:ring-theme-400"
        [ngClass]="idOf(profile) === editingId ? 'bg-theme-100' : 'hover:bg-theme-50'"
      >
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-1.5">
            <span class="truncate text-sm" [ngClass]="idOf(profile) === editingId ? 'font-medium' : ''" [matTooltip]="nameOf(profile)" matTooltipShowDelay="600">{{
              nameOf(profile)
            }}</span>
            <span
              *ngIf="isActive(profile)"
              class="shrink-0 rounded-full bg-status-green-soft px-1.5 text-xs font-medium text-status-green"
              >{{ 'Profiles.Active' | translate }}</span
            >
          </div>
          <div class="truncate text-xs text-gray-500">
            <ng-container *ngIf="profile.isPersonal; else orgMeta">
              {{ (profile.isPrivate ? 'General.Private' : 'General.Public') | translate }}
            </ng-container>
            <ng-template #orgMeta>{{ profile.owner }}</ng-template>
            <ng-container *ngIf="profile.isReadOnly"> · {{ 'General.ViewOnly' | translate }}</ng-container>
            <ng-container *ngIf="profile.modifiedOn">
              · {{ 'Profiles.EditedOn' | translate: { date: (profile.modifiedOn | date: 'mediumDate') } }}
            </ng-container>
          </div>
        </div>

        <!-- Hidden until the row is hovered, focused or open, so a long list does not read as a
             wall of icons. Stops propagation: the menu must not also open the profile. -->
        <button
          mat-icon-button
          type="button"
          class="shrink-0 text-gray-500 group-hover:opacity-100 group-focus-within:opacity-100"
          [ngClass]="idOf(profile) === editingId ? 'opacity-100' : 'opacity-0'"
          [matMenuTriggerFor]="rowMenu"
          [matMenuTriggerData]="{ profile: profile }"
          [attr.aria-label]="'Profiles.MoreActions' | translate"
          (click)="$event.stopPropagation()"
          (keydown.enter)="$event.stopPropagation()"
          (keydown.space)="$event.stopPropagation()"
        >
          <mat-icon>more_horiz</mat-icon>
        </button>
      </div>
      </ng-container>
    </section>

    <p *ngIf="hasProfiles && groups.length === 0" class="m-0 px-2 py-6 text-center text-sm text-gray-500">
      {{ 'Profiles.NoProfilesMatch' | translate }}
    </p>

    <div *ngIf="!hasProfiles && !loading" class="flex flex-col items-center gap-3 px-2 py-10 text-center text-sm text-gray-500">
      {{ 'Profiles.NoPersonalProfilesFound' | translate }}
      <button mat-flat-button color="primary" type="button" (click)="createNewProfile()">
        {{ 'FilterProfile.Create' | translate }}
      </button>
    </div>
  </div>
</div>

<mat-menu #rowMenu="matMenu" xPosition="before">
  <ng-template matMenuContent let-profile="profile">
    <button mat-menu-item type="button" (click)="copyProfile(profile)">
      <mat-icon>content_copy</mat-icon>
      <span>{{ 'General.Copy' | translate }}</span>
    </button>
    <button mat-menu-item type="button" *ngIf="!profile.isReadOnly" (click)="deleteProfile(profile)">
      <mat-icon>delete</mat-icon>
      <span>{{ 'General.Delete' | translate }}</span>
    </button>
  </ng-template>
</mat-menu>
